<template>
    <!-- Install Prompt Banner -->
    <div v-if="showPrompt" class="install-prompt">
      <div class="prompt-content">
        <div style="display: flex; justify-content: flex-start; align-items: flex-start; gap: 12px;">
          <img :src="getAssetUrl('assets/icon.png')" style="flex-shrink: 0; width: 48px; border-radius: 4px;"/>
          <div>
            <!-- Messaggio personalizzato per iOS -->
            <p v-if="isIOS() && !isSafari()">Utilizza safari per installare l'app</p>
            <p v-else-if="isIOS() && !isInstalledButInBrowser">
              Tocca <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ios-share-icon">
                   <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                   <polyline points="16 6 12 2 8 6"></polyline>
                   <line x1="12" y1="2" x2="12" y2="15"></line>
                 </svg> e poi "Aggiungi a Home" per installare l'applicazione
            </p>
            <!-- Messaggio personalizzato per Android -->
            <p v-else-if="isAndroid() && !isInstalledButInBrowser">
              Tocca <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="android-menu-icon">
                   <circle cx="12" cy="12" r="1"></circle>
                   <circle cx="12" cy="5" r="1"></circle>
                   <circle cx="12" cy="19" r="1"></circle>
                 </svg> e poi "Installa app" o "Aggiungi a schermata Home"
            </p>
            <!-- Messaggio per app già installata -->
            <p v-else-if="isInstalledButInBrowser">Hai già installato l'app. Aprila ora.</p>
            <!-- Messaggio generico per altri casi -->
            <p v-else>Per una migliore esperienza di navigazione, installa l'app sul dispositivo</p>
          </div>
        </div>
        <div>
          <button @click="closePrompt" class="btn_close_popup">
            Chiudi
          </button>
        </div>
      </div>
    </div>

    <!-- Notification Prompt Modal -->
    <div v-if="showNotificationPrompt" class="notification-prompt-overlay" @click.self="handleNotificationPromptClose">
      <div class="notification-prompt-modal">
        <div class="notification-prompt-header">
          <img :src="getAssetUrl('assets/icon.png')" class="notification-prompt-icon" alt="App Icon"/>
          <h3 class="notification-prompt-title">Abilita le notifiche</h3>
        </div>
        <div class="notification-prompt-body">
          <p class="notification-prompt-message">
            Vuoi ricevere notifiche push per rimanere sempre aggiornato sulle novità dell'app?
          </p>
        </div>
        <div class="notification-prompt-actions">
          <button @click="handleNotificationPromptClose" class="btn_notification_rimanda" :disabled="isEnablingNotifications">
            Rimanda
          </button>
          <button @click="handleEnableNotifications" class="btn_notification_abilita" :disabled="isEnablingNotifications">
            <span v-if="isEnablingNotifications">Abilitazione...</span>
            <span v-else>Abilita notifiche</span>
          </button>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, onBeforeUnmount } from 'vue';
  import { alertController } from "@ionic/vue";
  import { getAssetUrl } from "@/utils";
  import { getUser } from "@/services/userStore";
  import { openToast } from "@/services/toast";
  import apiPush from "@/services/push";
  import apiMobileSettings from "@/services/app_mobile_settings";
  
  // Configuration
  const localStorageKey = 'pwa_installed';
  const notificationPromptKey = 'notification_prompt_shown';
  const firstStandaloneOpenKey = 'first_standalone_open';

  // Funzione di debug per resettare i flag delle notifiche
  // Chiama resetNotificationFlags() dalla console del browser per resettare durante i test
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    window.resetNotificationFlags = () => {
      localStorage.removeItem(notificationPromptKey);
      localStorage.removeItem(firstStandaloneOpenKey);
      console.log('Flag notifiche resettati. Ricarica la pagina.');
    };
  }
  
  // State
  const showPrompt = ref(false);
  const isInstalledButInBrowser = ref(false);
  const canInstall = ref(false);
  const deferredPrompt = ref(null);
  const showNotificationPrompt = ref(false);
  const isEnablingNotifications = ref(false);
  
  // Helpers
  const isStandalone = () => {
    // Android Chrome
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    // iOS Safari - standalone è una proprietà non standard
    const nav = window.navigator;
    if ('standalone' in nav) {
      return nav['standalone'] === true;
    }
    return false;
  };
  
  const isSafari = () => {
    const ua = window.navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(ua);
  };

  const isIOS = () => {
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  };
  
  const isAndroid = () => {
    return /android/i.test(window.navigator.userAgent);
  };
  
  const isInBrowser = () => {
    return !isStandalone();
  };
  
  // Check if we should remember this user has installed
  const checkStoredInstallState = () => {
    const stored = localStorage.getItem(localStorageKey);
    if (stored) {
      const data = JSON.parse(stored);
      // Check if it's an iOS device that we previously marked as installed
      if (isIOS() && data.ios) {
        isInstalledButInBrowser.value = true;
        return true;
      }
      // Check if it's an Android device that we previously marked as installed
      if (isAndroid() && data.android) {
        isInstalledButInBrowser.value = true;
        return true;
      }
    }
    return false;
  };
  
  const storeInstallState = (installed = false) => {
    if (installed) {
      const currentData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
      const data = { ...currentData };
      
      if (isIOS()) {
        data.ios = true;
      }
      if (isAndroid()) {
        data.android = true;
      }
      
      localStorage.setItem(localStorageKey, JSON.stringify(data));
    }
    // Non memorizziamo nulla se l'utente ha chiuso il banner
  };
  
  const checkInstallStatus = async () => {
    if (isStandalone()) {
      showPrompt.value = false;
      return;
    }
    
    // Check stored state first
    if (checkStoredInstallState()) {
      showPrompt.value = isInstalledButInBrowser.value;
      return;
    }
    
    // Try to detect if app is installed
    if ('getInstalledRelatedApps' in navigator) {
      // Android con Chrome
      try {
        const apps = await navigator.getInstalledRelatedApps();
        isInstalledButInBrowser.value = apps.length > 0;
        if (isInstalledButInBrowser.value) {
          storeInstallState(true);
        }
      } catch (error) {
        console.error('Error checking installed apps:', error);
      }
    } else if (isIOS()) {
      // Per iOS, dobbiamo usare altri metodi per rilevare l'installazione
      isInstalledButInBrowser.value = false;
      // Controlla localStorage per vedere se abbiamo già registrato un'installazione
      const data = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
      if (data.ios) {
        isInstalledButInBrowser.value = true;
      }
    } else {
      // Fallback generico
      isInstalledButInBrowser.value = false;
    }
    
    showPrompt.value = true;
  };
  
  
  const closePrompt = async () => {
    const alert = await alertController.create({
      header: "Conferma",
      message: "Stai per chiudere le istruzioni per installare l'app. Sei sicuro?",
      backdropDismiss: false,
      buttons: [
        {
          text: "Annulla",
          role: "cancel",
        },
        {
          text: "Chiudi",
          role: "confirm",
          handler: () => {
            // Chiudiamo temporaneamente il banner
            showPrompt.value = false;
          },
        },
      ],
    });
  
    await alert.present();
  };
  
  // Event handlers
  const handleBeforeInstallPrompt = (e) => {
    e.preventDefault();
    deferredPrompt.value = e;
    canInstall.value = true;
  };
  
  // Check if notification prompt has already been shown
  const hasNotificationPromptBeenShown = () => {
    const stored = localStorage.getItem(notificationPromptKey);
    return stored === 'true';
  };

  // Converte la VAPID public key in Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const handleAppInstalled = () => {
    storeInstallState(true);
    deferredPrompt.value = null;
    canInstall.value = false;
    showPrompt.value = false;
    
    // Marca che è la prima apertura in standalone dopo installazione
    localStorage.setItem(firstStandaloneOpenKey, 'true');
    
    // Check if notification prompt should be shown (only first time)
    if (!hasNotificationPromptBeenShown()) {
      // Mostra popup per abilitare notifiche dopo un breve delay
      setTimeout(() => {
        showNotificationPrompt.value = true;
      }, 500);
    }
  };

  // Controlla se è la prima apertura in standalone mode (per iOS che non emette sempre appinstalled)
  const checkFirstStandaloneOpen = () => {
    // Verifica che le notifiche siano supportate prima di mostrare il popup
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return;
    }

    if (isStandalone()) {
      const firstOpen = localStorage.getItem(firstStandaloneOpenKey);
      if (!firstOpen) {
        // Prima volta che apriamo in standalone, potrebbe essere una nuova installazione
        localStorage.setItem(firstStandaloneOpenKey, 'true');
        
        // Mostra popup solo se non è mai stato mostrato
        if (!hasNotificationPromptBeenShown()) {
          setTimeout(() => {
            showNotificationPrompt.value = true;
          }, 1000);
        }
      }
    }
  };


  const handleNotificationPromptClose = () => {
    // Salva il flag per non mostrare più il popup
    localStorage.setItem(notificationPromptKey, 'true');
    showNotificationPrompt.value = false;
    
    // Emetti evento personalizzato per aggiornare il pulsante nella sidebar (stessa tab)
    window.dispatchEvent(new Event('notificationPromptClosed'));
  };

  const handleEnableNotifications = async () => {
    // Verifica supporto browser
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      openToast("Le notifiche push non sono supportate su questo dispositivo", "toast_danger");
      handleNotificationPromptClose();
      return;
    }

    // Verifica utente loggato
    const user = getUser();
    if (!user || !user.dipendenti_user_id) {
      openToast("Devi essere loggato per abilitare le notifiche", "toast_danger");
      handleNotificationPromptClose();
      return;
    }

    isEnablingNotifications.value = true;

    try {
      // Richiedi permesso notifiche
      const permission = await Notification.requestPermission();

      if (permission !== "granted") {
        openToast("Permesso notifiche negato", "toast_danger");
        handleNotificationPromptClose();
        return;
      }

      // Attendi che il service worker sia pronto
      const registration = await navigator.serviceWorker.ready;

      // Recupera la VAPID public key dal backend
      const vapidResponse = await apiMobileSettings.getVapidPublicKey();

      if (vapidResponse.data.status !== 0 || !vapidResponse.data.data || !vapidResponse.data.data.length) {
        openToast("Chiavi VAPID non configurate nel CRM", "toast_danger");
        handleNotificationPromptClose();
        return;
      }

      const vapidPublicKey = vapidResponse.data.data[0].app_mobile_settings_vapid_public_key;

      if (!vapidPublicKey) {
        openToast("Chiave VAPID mancante nelle impostazioni app mobile", "toast_danger");
        handleNotificationPromptClose();
        return;
      }

      // Crea la subscription push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      const subJson = subscription.toJSON();

      // Salva la subscription nel CRM
      try {
        await apiPush.saveSubscription(
          user.dipendenti_user_id,
          subJson.endpoint,
          subJson.keys?.p256dh,
          subJson.keys?.auth
        );
        
        openToast("Notifiche push abilitate correttamente", "toast_success");
        handleNotificationPromptClose();
        
        // Emetti evento per aggiornare il pulsante nella sidebar
        window.dispatchEvent(new Event('notificationsEnabled'));
      } catch (e) {
        console.error(e);
        openToast("Errore nel salvataggio della sottoscrizione push", "toast_danger");
        handleNotificationPromptClose();
      }
    } catch (error) {
      console.error(error);
      openToast("Errore durante l'attivazione delle notifiche push", "toast_danger");
      handleNotificationPromptClose();
    } finally {
      isEnablingNotifications.value = false;
    }
  };
  
  onMounted(() => {
    // Listen for 'beforeinstallprompt' event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    // Listen for 'appinstalled' event
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Check if browser or standalone
    if (isInBrowser()) {
      checkInstallStatus();
    } else {
      // Se siamo in standalone, controlla se è la prima apertura (per iOS)
      checkFirstStandaloneOpen();
    }
  });
  
  onBeforeUnmount(() => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', handleAppInstalled);
  });
  </script>
  
  <style scoped>
  .install-prompt {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #ffffff;
    padding: 12px;
    z-index: 9999;
    display: flex;
    justify-content: center;
    box-shadow: 0px -3px 12px rgba(0, 0, 0, 0.1);
  }
  
  .prompt-content {
    max-width: 600px;
    width: 100%;
    text-align: center;
  }
  
  .ios-share-icon, .android-menu-icon {
    display: inline-block;
    vertical-align: middle;
    margin: 0 2px;
  }
  
  .btn_install_app {
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 10px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    background-color: rgb(209 250 229);
    color: rgb(16 185 129);
    border: none;
    margin-top: 8px;
  }
  
  .btn_close_popup {
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 10px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    background-color: rgb(254 226 226);
    color: rgb(239 68 68);
    border: none;
    margin-top: 8px;
  }

  /* Notification Prompt Modal Styles */
  .notification-prompt-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    padding: 16px;
  }

  .notification-prompt-modal {
    background: #ffffff;
    border-radius: 4px;
    max-width: 400px;
    width: 100%;
    box-shadow: 0px 2px 3px 0px rgba(58, 58, 58, 0.1);
    overflow: hidden;
  }

  .notification-prompt-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 16px 16px;
  }

  .notification-prompt-icon {
    width: 64px;
    height: 64px;
    border-radius: 4px;
    margin-bottom: 12px;
  }

  .notification-prompt-title {
    font-size: 18px;
    font-weight: 600;
    color: #475569;
    margin: 0;
  }

  .notification-prompt-body {
    padding: 20px 16px;
  }

  .notification-prompt-message {
    font-size: 14px;
    color: #475569;
    line-height: 1.5;
    margin: 0;
    text-align: center;
  }

  .notification-prompt-actions {
    display: flex;
    gap: 8px;
    padding: 12px 16px 16px;
  }

  .btn_notification_rimanda {
    flex: 1;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 4px;
    background-color: #f2f2f2;
    color: #475569;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn_notification_rimanda:hover:not(:disabled) {
    background-color: #e2e8f0;
  }

  .btn_notification_rimanda:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn_notification_abilita {
    flex: 1;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 4px;
    background-color: #086fa3;
    color: #ffffff;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn_notification_abilita:hover:not(:disabled) {
    background-color: #075a87;
  }

  .btn_notification_abilita:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  </style>